import CarotekColors from "@carotek/utils/Colors";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { getPageByType, PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    const newsParentPage = getDataView(state.data.pages, { parentNodeId: nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (newsParentPage.value) {
        links = newsParentPage.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        newsParentPage,
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

const enum fields {
    nodeId = "nodeId",
    overrideTitle = "overrideTitle",
    defaultPageSize = "defaultPageSize",
    hidePagination = "hidePagination",
}

export interface Props extends WidgetProps {
    fields: {
        [fields.overrideTitle]: string;
        [fields.nodeId]: LinkFieldValue;
        [fields.defaultPageSize]: number;
        [fields.hidePagination]: boolean;
    };
}

export interface ItemListStyles {
    itemListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
}

export const itemListStyles: ItemListStyles = {
    itemListWrapper: {
        css: css`
            text-align: right;
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    linkWrapper: {
        css: css`
            border: 1px solid ${CarotekColors.grey21};
            padding: 15px;
            margin-bottom: 30px;
            box-shadow: 0 2px 4px ${CarotekColors.grey21};
        `,
    },
    title: {
        size: 22,
        fontFamily: "Neuron",
        transform: "capitalize",
        weight: 700,
        css: css`
            margin-bottom: 20px;
        `,
    },
    titleLink: {
        css: css`
            margin-bottom: 20px;
        `,
        typographyProps: {
            transform: "uppercase",
            size: 28,
            color: "primary",
            fontFamily: "NeuronBold",
            weight: 700,
            lineHeight: "26px",
            css: css`
                :hover {
                    text-decoration: underline;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 20px;
                                line-height: 24px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    summaryWrapper: {
        css: css`
            > div {
                display: flex;
                gap: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-direction: column;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}

                span[class^="LazyImageStyle"] {
                    width: 13%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    width: 60%;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }

                span:not([class]),
                .desc-wrap {
                    overflow: hidden;
                    max-height: 75px;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    font-size: 14px;
                    text-transform: none;
                    width: 84%;
                    line-height: 25.6px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    width: 100%;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }

                .desc-wrap {
                    max-height: inherit;
                    -webkit-line-clamp: 100;
                }
            }
        `,
    },
};

type MyProps = Props & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const ItemList = ({
    fields: { overrideTitle, nodeId, defaultPageSize, hidePagination },
    newsParentPage,
    loadPagesForParent,
    parameter,
}: MyProps) => {
    const rootPageLink = useAppSelector(state => getPageLinkByNodeId(state, nodeId?.value));
    const pages = useAppSelector(state => getDataView(state.data.pages, { parentNodeId: rootPageLink?.id }));

    const styles = useMergeStyles("itemList", itemListStyles, newsParentPage);

    const [currentPage, setCurrentPage] = useState(1);
    const startingPageSize = defaultPageSize ?? 5;
    const [currentPageSize, setCurrentPageSize] = useState(startingPageSize);
    useEffect(() => {
        setCurrentPage(1);
        setCurrentPageSize(defaultPageSize ?? 5);
    }, [defaultPageSize]);

    const pageSizes = [startingPageSize, startingPageSize * 2, startingPageSize * 3, startingPageSize * 4];

    const filteredPages = rootPageLink?.children?.filter(o => !o.excludeFromNavigation);

    useEffect(() => {
        if (!newsParentPage.value && rootPageLink?.id) {
            loadPagesForParent({
                parentNodeId: rootPageLink?.id,
            });
        }
    }, [newsParentPage, rootPageLink]);

    if (!rootPageLink || !filteredPages || filteredPages.length === 0) {
        return null;
    }

    return (
        <StyledWrapper css={styles.itemListWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h4" {...styles.title}>
                    {overrideTitle || rootPageLink.title}
                </Typography>
            </StyledWrapper>
            {filteredPages.map((link, index) => {
                if (index < (currentPage - 1) * currentPageSize || index >= currentPage * currentPageSize) {
                    return;
                }
                const newsBlock = pages.value?.find(page => page.nodeId === link.id);

                return (
                    <StyledWrapper key={link.url} {...styles.linkWrapper}>
                        {link.url && (
                            <Link {...styles.titleLink} href={link.url}>
                                {link.title}
                            </Link>
                        )}
                        {newsBlock?.translatableFields?.newsSummary &&
                        Object.values(newsBlock?.translatableFields?.newsSummary).length ? (
                            <StyledWrapper {...styles.summaryWrapper}>
                                {parse(Object.values(newsBlock?.translatableFields?.newsSummary)[0], parserOptions)}
                            </StyledWrapper>
                        ) : newsBlock?.translatableFields?.metaDescription &&
                          Object.values(newsBlock?.translatableFields?.metaDescription).length ? (
                            <StyledWrapper {...styles.summaryWrapper}>
                                <StyledWrapper>
                                    <span className="desc-wrap">
                                        {parse(
                                            Object.values(newsBlock?.translatableFields?.metaDescription)[0],
                                            parserOptions,
                                        )}
                                    </span>
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : null}
                    </StyledWrapper>
                );
            })}
            {!hidePagination && (
                <Pagination
                    {...styles.pagination}
                    resultsCount={filteredPages.length}
                    currentPage={currentPage}
                    resultsPerPage={currentPageSize}
                    resultsPerPageOptions={pageSizes}
                    onChangePage={setCurrentPage}
                    onChangeResultsPerPage={(event, pageSize) => setCurrentPageSize(pageSize)}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ItemList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.hidePagination,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                fieldType: "General",
                allowUrls: () => false,
                allowCategories: () => false,
            },
        ],
    },
};

export default widgetModule;
